import { getTenantClient } from '@sazeo/supabase-tenant';
import type { Session as SupabaseSession, User as SupabaseUser } from '@supabase/supabase-js';

/**
 * Vendor-neutral session type. Shape is intentionally minimal — if we swap
 * Supabase for another auth provider we only need to re-map to this contract.
 */
export type AuthSession = {
  userId: string;
  email: string | null;
  activeTenantId: string | null;
  roleName?: string | null;
  accessToken: string;
  refreshToken: string;
  expiresAt: number | null; // unix seconds
};

/**
 * Vendor-neutral error shape. We never leak Supabase's `AuthError` — callers
 * only ever see `{ code, message }`.
 */
export type AuthError = {
  code: string;
  message: string;
};

export type OAuthProvider = 'google' | 'apple' | 'github';

type SignInInput = {
  email: string;
  password: string;
};

type SignUpInput = {
  email: string;
  password: string;
  displayName?: string;
  roleName?: string;
};

type MagicLinkInput = {
  email: string;
  redirectTo?: string;
};

type UpdatePasswordInput = {
  password: string;
};

/**
 * Type guard — discriminates `AuthSession` from `AuthError` on union returns.
 */
export function isAuthError(value: AuthSession | AuthError): value is AuthError {
  return (
    (value as AuthError).code !== undefined && (value as AuthSession).accessToken === undefined
  );
}

function decodeJwtClaims(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    if (!payload) return {};
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function readRoleString(value: unknown): string | null {
  const role = readString(value);
  return role && !['anon', 'authenticated', 'service_role'].includes(role) ? role : null;
}

function readSelfSelectedRoleString(value: unknown): string | null {
  const role = readString(value);
  return role === 'candidate' || role === 'freelancer' || role === 'hr_manager' ? role : null;
}

function mapSupabaseSession(
  session: SupabaseSession | null | undefined,
  user?: SupabaseUser | null,
): AuthSession | null {
  if (!session) return null;
  const u = user ?? session.user;
  const claims = decodeJwtClaims(session.access_token);
  const activeTenantId =
    readString(claims.tenant_id) ??
    readString(claims.active_tenant_id) ??
    readString(u?.app_metadata?.active_tenant_id) ??
    readString(u?.user_metadata?.active_tenant_id) ??
    null;
  const roleName =
    readString(claims.role_name) ??
    readString(claims.tenant_role) ??
    readString(u?.app_metadata?.role_name) ??
    readString(u?.app_metadata?.tenant_role) ??
    readRoleString(claims.role) ??
    readSelfSelectedRoleString(u?.user_metadata?.role_name) ??
    readSelfSelectedRoleString(u?.user_metadata?.tenant_role) ??
    null;

  return {
    userId: u?.id ?? '',
    email: u?.email ?? null,
    activeTenantId,
    roleName,
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    expiresAt: session.expires_at ?? null,
  };
}

/**
 * Append the session's access + refresh tokens to a URL hash so the destination
 * subdomain's Supabase client (with `detectSessionInUrl: true`) can pick them
 * up and persist locally. This is how we hand off sign-in across subdomains
 * (app.sazeo.io → {slug}.app.sazeo.io), where localStorage is per-origin.
 *
 * Format mirrors Supabase's own redirect-with-tokens convention used by
 * OAuth callbacks and magic links.
 */
export function appendSessionToUrl(
  url: string,
  session: AuthSession,
  type: 'signup' | 'login' | 'magiclink' = 'login',
): string {
  const params = new URLSearchParams({
    access_token: session.accessToken,
    refresh_token: session.refreshToken,
    token_type: 'bearer',
    type,
  });
  if (session.expiresAt) {
    const expiresIn = Math.max(0, session.expiresAt - Math.floor(Date.now() / 1000));
    params.set('expires_in', String(expiresIn));
    params.set('expires_at', String(session.expiresAt));
  }
  const sep = url.includes('#') ? '&' : '#';
  return `${url}${sep}${params.toString()}`;
}

function mapSupabaseError(err: { code?: string; name?: string; message: string }): AuthError {
  return {
    code: err.code ?? err.name ?? 'auth_error',
    message: err.message,
  };
}

export async function signInWithPassword(input: SignInInput): Promise<AuthSession | AuthError> {
  const client = getTenantClient();
  const { data, error } = await client.auth.signInWithPassword({
    email: input.email,
    password: input.password,
  });

  if (error) return mapSupabaseError(error);
  const mapped = mapSupabaseSession(data.session, data.user);
  if (!mapped) {
    return { code: 'no_session', message: 'Sign-in succeeded but no session was returned.' };
  }
  return mapped;
}

export async function signUpWithPassword(input: SignUpInput): Promise<AuthSession | AuthError> {
  const client = getTenantClient();
  const roleName = readSelfSelectedRoleString(input.roleName);
  const metadata = {
    ...(input.displayName ? { display_name: input.displayName } : {}),
    ...(roleName ? { role_name: roleName } : {}),
  };
  const { data, error } = await client.auth.signUp({
    email: input.email,
    password: input.password,
    options: Object.keys(metadata).length ? { data: metadata } : undefined,
  });

  if (error) return mapSupabaseError(error);
  const mapped = mapSupabaseSession(data.session, data.user);
  if (!mapped) {
    // Email-confirmation flow — user exists but no session until they verify.
    return {
      code: 'email_confirmation_required',
      message: 'Sign-up succeeded; check email to confirm before signing in.',
    };
  }
  return mapped;
}

/**
 * Redirects to the provider's OAuth consent screen. Supabase handles the
 * callback on `/auth/callback` (detectSessionInUrl is on in the client).
 *
 * Pass `redirectTo` so Supabase returns to the correct origin after OAuth.
 * The URL must be listed in the Supabase dashboard → Auth → URL Configuration.
 */
export async function signInWithOAuth(
  provider: OAuthProvider,
  opts?: { redirectTo?: string },
): Promise<void> {
  const client = getTenantClient();
  const { error } = await client.auth.signInWithOAuth({
    provider,
    options: opts?.redirectTo ? { redirectTo: opts.redirectTo } : undefined,
  });
  if (error) {
    throw new Error(`[@sazeo/auth] OAuth redirect failed: ${error.message}`);
  }
}

export async function sendMagicLink(input: MagicLinkInput): Promise<void> {
  const client = getTenantClient();
  const { error } = await client.auth.signInWithOtp({
    email: input.email,
    options: {
      shouldCreateUser: false,
      ...(input.redirectTo ? { emailRedirectTo: input.redirectTo } : {}),
    },
  });
  if (error) {
    throw new Error(`[@sazeo/auth] Magic link send failed: ${error.message}`);
  }
}

export async function updatePassword(input: UpdatePasswordInput): Promise<void> {
  const client = getTenantClient();
  const { error } = await client.auth.updateUser({ password: input.password });
  if (error) {
    throw new Error(`[@sazeo/auth] Password update failed: ${error.message}`);
  }
}

export async function signOut(): Promise<void> {
  const client = getTenantClient();
  const { error } = await client.auth.signOut();
  if (error) {
    throw new Error(`[@sazeo/auth] Sign-out failed: ${error.message}`);
  }
}

export async function getSession(): Promise<AuthSession | null> {
  const client = getTenantClient();
  const { data } = await client.auth.getSession();
  return mapSupabaseSession(data.session);
}

export async function refreshSession(): Promise<AuthSession | AuthError> {
  const client = getTenantClient();
  const { data, error } = await client.auth.refreshSession();
  if (error) return mapSupabaseError(error);
  const mapped = mapSupabaseSession(data.session, data.user);
  if (!mapped) {
    return {
      code: 'no_session',
      message: 'Session refresh succeeded but no session was returned.',
    };
  }
  return mapped;
}

/**
 * Subscribe to session changes (sign-in / sign-out / token refresh).
 * Returns an unsubscribe function — call it on unmount to avoid leaks.
 */
export function onAuthStateChange(callback: (session: AuthSession | null) => void): () => void {
  const client = getTenantClient();
  const { data } = client.auth.onAuthStateChange((_event, session) => {
    callback(mapSupabaseSession(session));
  });
  return () => {
    data.subscription.unsubscribe();
  };
}

/**
 * Inject a pre-fetched session (e.g. demo visitor tokens) directly into the
 * Supabase client without requiring a password sign-in.
 */
export async function setSession(tokens: {
  access_token: string;
  refresh_token: string;
}): Promise<void> {
  const client = getTenantClient();
  const { error } = await client.auth.setSession(tokens);
  if (error) {
    throw new Error(`[@sazeo/auth] setSession failed: ${error.message}`);
  }
}

/**
 * Switch the user's active tenant.
 *
 * Calls the SECDEF `set_active_tenant` RPC (migration 0096), which verifies
 * membership and writes `app_metadata.active_tenant_id` on auth.users. We
 * then refresh the session so the next-issued JWT carries the new claim.
 * `current_tenant_id()` (post-0094) reads either the top-level claim
 * populated by the optional auth-jwt-hook or the app_metadata fallback —
 * both routes work after this call.
 */
export async function setActiveTenant(tenantId: string): Promise<void> {
  if (!tenantId) {
    throw new Error('[@sazeo/auth] setActiveTenant requires a non-empty tenantId.');
  }
  const client = getTenantClient();
  const { error: rpcError } = await client.rpc('set_active_tenant', {
    p_tenant_id: tenantId,
  });
  if (rpcError) {
    throw new Error(`[@sazeo/auth] setActiveTenant RPC failed: ${rpcError.message}`);
  }
  const { error: refreshError } = await client.auth.refreshSession();
  if (refreshError) {
    throw new Error(`[@sazeo/auth] setActiveTenant refresh failed: ${refreshError.message}`);
  }
}
